import { useCallback, useRef, useState } from "react";

import type { StatusType } from "../../../components/status-indicator/StatusIndicator";

/** 進行中ステータス（自動クリアの対象外） */
const PROGRESS_STATUSES = new Set<StatusType>(["saving", "creating", "deleting"]);

/** `setStatus` を直接呼ぶ代わりに使う、終端状態の自動クリア時間 (ms)。 */
const DEFAULT_CLEAR_MS = 1500;

interface UseStatusIndicatorReturn {
  status: StatusType;
  /**
   * ステータスを更新する。終端状態（`saved` / `error` 等、進行中以外）は
   * 一定時間後に自動的に `idle` に戻す。複数回呼ばれた場合は前回のタイマーを
   * キャンセルし、最新の状態に対する再カウントとなる。
   *
   * @param next 新しいステータス
   * @param ms 自動 idle までの時間。デフォルト 1500ms。`error` などは長めに指定するとよい。
   */
  // eslint-disable-next-line no-unused-vars -- 関数シグネチャ引数名はドキュメント目的
  indicate: (next: StatusType, ms?: number) => void;
}

/**
 * `RolesManager` / `RoleTagsManager` / `CreatorRolesManager` 等で共通に
 * 使う「保存系操作のステータス → StatusIndicator 表示」のフック。
 *
 * - 進行中ステータス (`saving` / `creating` / `deleting`) は手動で次の状態を
 *   セットするまでクリアしない。
 * - 終端ステータス (`saved` / `error` / それ以外) は `ms` 経過後に `idle` に戻す。
 */
export const useStatusIndicator = (): UseStatusIndicatorReturn => {
  const [status, setStatus] = useState<StatusType>("idle");
  const timerRef = useRef<number | null>(null);

  const indicate = useCallback((next: StatusType, ms = DEFAULT_CLEAR_MS) => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setStatus(next);
    if (PROGRESS_STATUSES.has(next)) return;
    timerRef.current = window.setTimeout(() => {
      timerRef.current = null;
      setStatus("idle");
    }, ms);
  }, []);

  return { status, indicate };
};
